import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Leaf, Package } from "lucide-react";
import useAutoRefreshList from "@/hooks/useAutoRefreshList";
import SmsConsent from "./SmsConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["All", "Furniture", "Power Tools", "Electronics", "As Is/Salvaged Goods", "Other"];

export default function Marketplace() {
  const { items } = useAutoRefreshList("/marketplace");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [inquiry, setInquiry] = useState({ name: "", phone: "", email: "", message: "", fulfillment: "delivery", sms_consent: false, zip_code: "" });
  const [fee, setFee] = useState(null);
  useEffect(() => {
    if (!active || inquiry.fulfillment !== "delivery" || !/^\d{5}$/.test(inquiry.zip_code || "")) { setFee(null); return; }
    const t = setTimeout(() => axios.get(`${API}/marketplace/${active.id}/delivery-fee`, { params: { zip: inquiry.zip_code } }).then(({ data }) => setFee(data)).catch(() => setFee(null)), 350);
    return () => clearTimeout(t);
  }, [active, inquiry.fulfillment, inquiry.zip_code]);
  const { user: customer } = useCustomerAuth();
  useEffect(() => {
    if (customer) setInquiry((f) => ({ ...f, name: f.name || customer.name || "", email: f.email || customer.email || "" }));
  }, [customer]);
  const [submitting, setSubmitting] = useState(false);

  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!active) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        item_id: active.id,
        item_title: active.title,
        ...inquiry,
      });
      toast.success(inquiry.fulfillment === "pickup"
        ? "Inquiry sent — pickup details on the way."
        : `Inquiry sent — delivery fee $${Number(fee?.total ?? active.delivery_fee ?? 25).toFixed(2)}. We'll confirm within 2 hours.`);
      setActive(null);
      setInquiry({ name: "", phone: "", email: "", message: "", fulfillment: "delivery", sms_consent: false, zip_code: "" });
    } catch {
      toast.error("Could not send inquiry.");
    } finally { setSubmitting(false); }
  };

  return (
    <section id="marketplace" data-testid="marketplace-section" className="py-16 lg:py-24 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div className="max-w-3xl">
            <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B] mb-2">Reclaimed Finds</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-zinc-900 leading-tight">
              Slightly used. Fully restored. Saved from the landfill.
            </h2>
            <p className="mt-4 text-zinc-600 max-w-2xl">
              Every item here was headed for a dumpster before our crew pulled it, cleaned it, and certified it working.
              Buy it, we deliver locally.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md">
            <Leaf className="w-4 h-4" />
            <span className="text-xs font-bold">2,340 tons diverted since 2019</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`marketplace-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-xs font-bold border-2 transition-colors ${
                filter === f ? "bg-[#0C2E2B] text-white border-[#0C2E2B]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((it, idx) => (
            <article
              key={it.id}
              data-testid={`marketplace-item-card-${idx + 1}`}
              className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden card-lift"
            >
              <div className="relative">
                <img src={it.image} alt={it.title} className="w-full h-56 object-cover" />
                <div className="absolute top-3 left-3 bg-emerald-600 text-white text-[10px] font-tactical uppercase tracking-widest px-2 py-1 rounded">
                  {it.condition}
                </div>
                <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur text-emerald-800 text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1">
                  <Leaf className="w-3 h-3" /> Saved {it.co2_saved} CO₂
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-500">{it.category}</div>
                <h3 className="font-display font-black text-lg text-zinc-900 mt-1">{it.title}</h3>
                <p className="text-xs text-zinc-600 mt-2 line-clamp-2">{it.description}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {it.pickup_available && (
                    <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">Free pickup</span>
                  )}
                  {(it.delivery_fee ?? 0) > 0 && (
                    <span className="text-[10px] font-bold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded">+${Number(it.delivery_fee).toFixed(0)} delivery</span>
                  )}
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="font-mono-data font-black text-xl text-[#0C2E2B]">${it.price}</div>
                    <div className="text-[11px] text-zinc-500 line-through">retail ${it.retail}</div>
                  </div>
                  <Button
                    data-testid={`marketplace-inquire-btn-${idx + 1}`}
                    onClick={() => setActive(it)}
                    className="btn-kinetic rounded-md text-xs"
                  >
                    Inquire
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Package className="w-5 h-5 text-[#0C2E2B]" /> Inquire · {active?.title}
            </DialogTitle>
            <DialogDescription>Choose delivery or pickup, leave your details, and we'll confirm within 2 hours.</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <img src={active.image} alt={active.title} className="w-full h-40 object-cover rounded-md" />
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">{active.condition}</span>
                <span className="font-mono-data font-black text-[#0C2E2B]">${active.price}</span>
              </div>
              <form onSubmit={submitInquiry} className="space-y-2" data-testid="inquiry-form">
                <div>
                  <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-700 mb-1.5">Fulfillment</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="inquiry-fulfillment-delivery-btn"
                      onClick={() => setInquiry({ ...inquiry, fulfillment: "delivery" })}
                      className={`p-2 rounded-md border-2 text-left transition-colors ${inquiry.fulfillment === "delivery" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}
                    >
                      <div className="font-bold text-xs text-[#0C2E2B]">Delivery</div>
                      <div className="text-[10px] text-zinc-500">+${Number(active.delivery_fee ?? 25).toFixed(2)}</div>
                    </button>
                    <button
                      type="button"
                      data-testid="inquiry-fulfillment-pickup-btn"
                      disabled={!active.pickup_available}
                      onClick={() => active.pickup_available && setInquiry({ ...inquiry, fulfillment: "pickup" })}
                      className={`p-2 rounded-md border-2 text-left transition-colors ${inquiry.fulfillment === "pickup" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"} ${!active.pickup_available ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <div className="font-bold text-xs text-[#0C2E2B]">Pickup</div>
                      <div className="text-[10px] text-emerald-700">{active.pickup_available ? "FREE" : "unavailable"}</div>
                    </button>
                  </div>
                </div>
                <Input data-testid="inquiry-name-input" placeholder="Your name" required value={inquiry.name} onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })} />
                <Input data-testid="inquiry-phone-input" placeholder="Phone" required value={inquiry.phone} onChange={(e) => setInquiry({ ...inquiry, phone: e.target.value })} />
                <Input data-testid="inquiry-email-input" type="email" placeholder="Email (optional)" value={inquiry.email} onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })} />
                {inquiry.fulfillment === "delivery" && (
                  <div data-testid="inquiry-delivery-pricing" className="space-y-1.5">
                    <Input data-testid="inquiry-zip-input" inputMode="numeric" maxLength={5} placeholder="Delivery ZIP code" required value={inquiry.zip_code} onChange={(e) => setInquiry({ ...inquiry, zip_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} />
                    {fee ? (
                      <div className="text-xs bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid="inquiry-fee-result">
                        <div className="flex justify-between"><span className="text-zinc-600">Delivery{fee.known ? ` · ${fee.place} · ~${fee.miles} mi` : ""}</span><span className="font-bold text-[#0C2E2B]" data-testid="inquiry-fee-total">${Number(fee.total).toFixed(2)}</span></div>
                        {fee.mileage_fee > 0 && <div className="text-[10px] text-zinc-500">${Number(fee.base_fee).toFixed(2)} within {fee.radius_miles} mi of {fee.home_zip} + {fee.extra_miles} extra mi × ${Number(fee.per_mile).toFixed(2)}</div>}
                        {fee.known && fee.mileage_fee === 0 && <div className="text-[10px] text-zinc-500">Within {fee.radius_miles} miles of {fee.home_zip} — flat rate</div>}
                        {!fee.known && <div className="text-[10px] text-amber-700">{fee.message}</div>}
                      </div>
                    ) : <p className="text-[10px] text-zinc-500">Enter your ZIP for the exact delivery fee — flat within 25 mi of 55423, +$0.75/mi beyond.</p>}
                  </div>
                )}
                <Textarea data-testid="inquiry-message-input" placeholder={inquiry.fulfillment === "pickup" ? "When can you swing by?" : "Any delivery notes?"} value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} rows={2} />
                <SmsConsent checked={inquiry.sms_consent} onChange={(v) => setInquiry({ ...inquiry, sms_consent: v })} context="inquiry" testId="inquiry-sms-consent" />
                <div className="flex justify-between text-sm bg-zinc-50 rounded-md p-2">
                  <span className="text-zinc-600">Estimated total</span>
                  <span className="font-mono-data font-black text-[#0C2E2B]" data-testid="inquiry-total-estimate">
                    ${(Number(active.price) + (inquiry.fulfillment === "delivery" ? Number(active.delivery_fee ?? 25) : 0)).toFixed(2)}
                  </span>
                </div>
                <Button data-testid="inquiry-submit-btn" type="submit" disabled={submitting} className="btn-kinetic rounded-md w-full">
                  {submitting ? "Sending..." : "Send Inquiry"}
                </Button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
